import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Utensils, Wine, Heart } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Hero Slider Images
const heroImages = [
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/9mflczp9_Restaurant-Hallbergmoos-Italiener-27.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/detarmam_Restaurant-Hallbergmoos-Italiener-34.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/csyztogr_Restaurant-Hallbergmoos-Italiener-81.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/i6mnctya_Restaurant-Hallbergmoos-Italiener-86.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/85i2mhs2_Restaurant-Hallbergmoos-Italiener-87.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/y7xytwfd_Restaurant-Hallbergmoos-Italiener-96.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/r6bhwkj8_Restaurant-Hallbergmoos-Italiener-101.jpg',
  'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/sj8cxn6o_Restaurant-Hallbergmoos-Italiener-25.jpg'
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section with Slider */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Slider */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${heroImages[currentSlide]}')`
            }}
          />
        </AnimatePresence>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-olive-900/60" />
        
        {/* Slider Navigation Arrows */}
        <button
          onClick={prevSlide}
          data-testid="slider-prev"
          className="absolute left-4 md:left-8 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-300 text-cream"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          onClick={nextSlide}
          data-testid="slider-next"
          className="absolute right-4 md:right-8 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-300 text-cream"
        >
          <ChevronRight size={28} />
        </button>

        {/* Slider Dots */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              data-testid={`slider-dot-${index}`}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-gold w-8' 
                  : 'bg-cream/50 hover:bg-cream/80'
              }`}
            />
          ))}
        </div>
        
        {/* Content */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
          className="relative z-10 text-center px-4 max-w-4xl"
        >
          <motion.p
            variants={fadeInUp}
            className="font-accent text-gold text-2xl md:text-3xl mb-4"
          >
            Benvenuti
          </motion.p>
          <motion.h1
            variants={fadeInUp}
            className="font-heading text-5xl md:text-7xl text-cream mb-6 tracking-tight"
          >
            Il Girasole
          </motion.h1>
          <motion.p
            variants={fadeInUp}
            className="font-body text-cream/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            Authentische italienische Küche mit Leidenschaft zubereitet.
            Erleben Sie die Aromen der Toskana in eleganter Atmosphäre.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/menu"
              data-testid="hero-menu-btn"
              className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300 hover:tracking-[0.2em]"
            >
              Speisekarte
            </Link>
            <Link
              to="/contact"
              data-testid="hero-contact-btn"
              className="border-2 border-cream text-cream hover:bg-cream hover:text-olive-900 px-8 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300"
            >
              Kontakt
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="text-cream/60 animate-bounce" size={32} />
        </motion.div>
      </section>

      {/* Welcome Section */}
      <section data-testid="welcome-section" className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-accent text-terracotta text-xl mb-2">Mal schnell nach bella Italia</p>
              <h2 className="font-heading text-4xl md:text-5xl text-olive-900 mb-6">
                Ein Stück Italien
              </h2>
              <p className="font-body text-muted-foreground text-lg leading-relaxed mb-6">
                Gönnen Sie sich eine kleine Auszeit vom Alltag. Genießen Sie in unserem 
                italienischen Restaurant im Norden von Hallbergmoos kulinarische Spezialitäten 
                Italiens. Unsere Speisekarte bietet weit mehr als dennoch hervorragende Pizza 
                und Pasta. Es warten auf Sie zudem vorzügliche Fisch- und Fleischgerichte 
                nach traditionell italienischen Rezepten.
              </p>
              <p className="font-body text-muted-foreground text-lg leading-relaxed mb-8">
                Wir freuen uns darauf Sie bei uns im Il Girasole in Hallbergmoos begrüßen zu dürfen.
              </p>
              <Link
                to="/about"
                data-testid="welcome-about-link"
                className="inline-block bg-olive-900 hover:bg-olive-800 text-cream px-8 py-3 font-body uppercase text-sm tracking-widest transition-all duration-300"
              >
                Mehr erfahren
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="border-[8px] border-cream-dark p-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/sj8cxn6o_Restaurant-Hallbergmoos-Italiener-25.jpg"
                  alt="Il Girasole Restaurant mit Sonnenblumen"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gold px-6 py-4 shadow-lg">
                <p className="font-accent text-olive-900 text-2xl">Buon appetito</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section data-testid="highlights-section" className="py-24 md:py-32 bg-cream-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-terracotta text-xl mb-2">Was uns auszeichnet</p>
            <h2 className="font-heading text-4xl md:text-5xl text-olive-900">
              Unsere Philosophie
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Utensils,
                title: 'Frische Zutaten',
                description: 'Nur die besten saisonalen Produkte finden ihren Weg in unsere Küche. Viele Zutaten importieren wir direkt aus Italien.',
                image: 'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/0bkurpjk_Restaurant-Hallbergmoos-Italiener-80.jpg'
              },
              {
                icon: Heart,
                title: 'Mit Liebe gekocht',
                description: 'Jedes Gericht wird mit Hingabe und nach traditionellen Familienrezepten zubereitet. Kochen ist für uns eine Herzensangelegenheit.',
                image: 'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/y7xytwfd_Restaurant-Hallbergmoos-Italiener-96.jpg'
              },
              {
                icon: Wine,
                title: 'Erlesene Weine',
                description: 'Unsere Weinkarte umfasst handverlesene italienische Weine, die perfekt zu unseren Gerichten passen.',
                image: 'https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/6hw2i7d5_Restaurant-Hallbergmoos-Italiener-65.jpg'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                data-testid={`highlight-card-${index}`}
                className="bg-white shadow-[0_4px_20px_-2px_rgba(0,0,0,0.1)] hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                </div>
                <div className="p-8 text-center">
                  <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center mx-auto mb-4 -mt-12 relative z-10 border-4 border-white">
                    <item.icon className="text-terracotta" size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-2xl text-olive-900 mb-4">{item.title}</h3>
                  <p className="font-body text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-olive-900 text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_trattoria-girasole/artifacts/detarmam_Restaurant-Hallbergmoos-Italiener-34.jpg')`
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-accent text-gold text-2xl mb-4">Buon Appetito</p>
            <h2 className="font-heading text-4xl md:text-5xl text-cream mb-6">
              Entdecken Sie unsere Speisekarte
            </h2>
            <p className="font-body text-cream/80 text-lg mb-10 max-w-2xl mx-auto">
              Von klassischen Antipasti über hausgemachte Pasta bis hin zu 
              verführerischen Dolci – lassen Sie sich von unseren Kreationen inspirieren.
            </p>
            <Link
              to="/menu"
              data-testid="cta-menu-btn"
              className="inline-block bg-terracotta hover:bg-terracotta-dark text-white px-10 py-4 font-body uppercase text-sm tracking-widest transition-all duration-300"
            >
              Zur Speisekarte
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
